/**
 * Agent logs (the Acquisition plan's P4): the Logs tab of the Plant agents page, for platform admins (Sahas staff)
 * only. The Sahas Forge Agent sends a bundle of its logs and a diagnostics report daily, after an outage or a failed
 * update, when a platform admin asks, or by hand at the PC (agent 0.3.1, backend logs.go).
 *
 *   • The bundles, by plant PC (and by plant, for every org): when, why, size, the agent's version and its way to the
 *     cloud. Each PC's online history beside its bundles (the presence route; org-scoped, so for the org you are in).
 *   • A bundle opens a panel with the connection checks from its diagnostics.json in plain words (DNS, the connection,
 *     the secure connection and who issued the certificate, the PC's clock, the proxy, a web filter), read without a
 *     download (GET /v1/platform/agent-logs/{id}/diagnostics), and the files the bundle holds.
 *   • Download saves the zip (an authenticated fetch, then a file the browser saves).
 *   • "Ask for logs now" asks a PC for its logs: pending until a bundle sent because it was asked arrives, for at
 *     most ASK_WAIT_MS (the list is read every 10 s meanwhile), and forgotten when the org you are in changes.
 *
 * The views are pure (the tests call them); AgentLogsPanel, AgentHistory and LogBundleSheet hold the hooks.
 */
import { useMemo, useState, type ReactNode } from "react";
import { CheckCircle2, CircleMinus, Download, Loader2, ScrollText, XCircle } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card } from "@/lib/ui/card";
import { NativeSelect } from "@/lib/ui/native-select";
import { Sheet, SheetHeader } from "@/lib/ui/sheet";
import { useToast } from "@/lib/ui/toast";
import { cn } from "@/lib/ui/cn";
import { useOrg } from "@/lib/org-context";
import {
  AGENT_LOGS_LIMIT,
  type AgentLogBundle,
  type AgentLogDiagnostics,
  type AgentLogReason,
  type AgentPresenceEvent,
  type IngestAgent,
  useAgentLogDiagnosticsQuery,
  useAgentLogsQuery,
  useAgentPresenceQuery,
  useRequestAgentLogs,
} from "@/lib/api/ingest-agents";
import {
  REASON_WORDS,
  type AgentLogGroup,
  type AgentsState,
  type Ask,
  type AskState,
  type CheckTone,
  type LogsScope,
  anyWaiting,
  askAvailability,
  askStates,
  certWords,
  checkVerdict,
  checkWords,
  downloadAgentLog,
  healthWords,
  historyBlocked,
  lastOutageWords,
  logGroups,
  logsErrorWords,
  plantLabel,
  presenceEventWords,
  problemHeadline,
  proxyWords,
  reasonSentence,
  updateWordsOf,
  viaWords,
} from "@/lib/agent-logs";
import { agoWords, fmtSize, fmtWhen, presenceWords, type PresenceTone } from "@/lib/plant-agents";

const TH = "whitespace-nowrap px-3 py-2 text-left font-semibold text-on-surface-muted";
const ROW_ACTION = "h-7 px-2";
const REASON_TONE: Record<AgentLogReason, "warning" | "info" | "secondary"> = {
  problem: "warning",
  request: "info",
  manual: "secondary",
  daily: "secondary",
  explore: "info",
};
const TONE_TEXT: Record<CheckTone, string> = { ok: "text-success", fail: "text-danger", skip: "text-on-surface-muted" };
const PRESENCE_DOT: Record<PresenceTone, string> = { online: "bg-success", paused: "bg-warning", offline: "bg-danger" };

function ToneIcon({ tone }: { tone: CheckTone }) {
  const Icon = tone === "ok" ? CheckCircle2 : tone === "fail" ? XCircle : CircleMinus;
  return <Icon aria-hidden className={cn("mt-0.5 size-3.5 shrink-0", TONE_TEXT[tone])} />;
}

// ── The list (pure) ──────────────────────────────────────────────────────────

export interface AgentLogsViewProps {
  scope: LogsScope;
  onScope: (s: LogsScope) => void;
  state: "loading" | "error" | "ready";
  error?: string;
  groups: AgentLogGroup[];
  /** The list holds the API's most: older bundles are not shown. */
  truncated: boolean;
  now: number;
  /** The org's PCs, as the Plant agents page read them: a PC the list does not hold can't be told from one still loading. */
  agentsState: AgentsState;
  asks: Record<string, AskState>;
  onAsk: (g: AgentLogGroup) => void;
  onOpen: (b: AgentLogBundle) => void;
  onDownload: (b: AgentLogBundle) => void;
  /** The bundle being downloaded, or null. */
  downloading: string | null;
  /** The PC's online history, beside its bundles; absent in tests of the list. */
  history?: (g: AgentLogGroup) => ReactNode;
}

function AskLine({ ask, onOpen }: { ask: AskState | undefined; onOpen: (b: AgentLogBundle) => void }) {
  if (!ask || ask.state === "sending") return null;
  if (ask.state === "expired") {
    return (
      <p className="text-xs text-warning" data-ask="expired">
        No logs have come since the ask at {fmtWhen(new Date(ask.at).toISOString())}. The PC may be off, asleep or cut off
        from Sahas Forge. Ask again to wait for them again.
      </p>
    );
  }
  if (ask.state === "done") {
    return (
      <p className="text-xs text-success" data-ask="done">
        The logs arrived {fmtWhen(ask.bundle.created_at)}.{" "}
        <button type="button" className="font-medium underline-offset-2 hover:underline" onClick={() => onOpen(ask.bundle)}>
          Read the checks
        </button>
      </p>
    );
  }
  return (
    <p className="flex items-start gap-1.5 text-xs text-on-surface-muted" data-ask="pending">
      <Loader2 aria-hidden className="mt-0.5 size-3.5 shrink-0 animate-spin" />
      <span>
        Asked at {fmtWhen(new Date(ask.at).toISOString())}.{" "}
        {ask.offline
          ? "The PC is offline: it sends its logs once it is back."
          : "Waiting for the PC: it sends its logs on its next sync, within seconds while it is online."}
      </span>
    </p>
  );
}

function BundlesTable({
  bundles,
  now,
  onOpen,
  onDownload,
  downloading,
}: {
  bundles: AgentLogBundle[];
  now: number;
  onOpen: (b: AgentLogBundle) => void;
  onDownload: (b: AgentLogBundle) => void;
  downloading: string | null;
}) {
  if (bundles.length === 0) return <p className="text-sm text-on-surface-muted">No bundles from this PC yet.</p>;
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full min-w-[36rem] text-sm">
        <thead className="border-b border-border">
          <tr>
            <th className={TH}>Sent</th>
            <th className={TH}>Why</th>
            <th className={TH}>Size</th>
            <th className={TH}>Agent</th>
            <th className="px-3 py-2">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {bundles.map((b) => {
            const why = REASON_WORDS[b.reason] ?? { label: b.reason, title: b.reason };
            const via = viaWords(b.via);
            return (
              <tr key={b.id} data-bundle={b.id} className="border-b border-border last:border-b-0 odd:bg-surface-elevated/30">
                <td className="whitespace-nowrap px-3 py-2">
                  <div className="text-on-surface">{fmtWhen(b.created_at)}</div>
                  <div className="text-xs text-on-surface-muted">{agoWords(b.created_at, now)}</div>
                </td>
                <td className="px-3 py-2">
                  <Badge variant={REASON_TONE[b.reason] ?? "secondary"} className="whitespace-nowrap" title={why.title}>
                    {why.label}
                  </Badge>
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-xs text-on-surface-muted">{fmtSize(b.size)}</td>
                <td className="px-3 py-2 text-xs text-on-surface-muted">
                  {b.agent_version ? `version ${b.agent_version}` : "version not given"}
                  {via && <div>sent {via}</div>}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={ROW_ACTION}
                    aria-label={`Read the checks in the bundle of ${fmtWhen(b.created_at)}`}
                    onClick={() => onOpen(b)}
                  >
                    Read the checks
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={ROW_ACTION}
                    disabled={downloading === b.id}
                    aria-label={`Download the bundle of ${fmtWhen(b.created_at)}`}
                    onClick={() => onDownload(b)}
                  >
                    {downloading === b.id ? <Loader2 aria-hidden className="animate-spin" /> : <Download aria-hidden />}
                    Download
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function AgentLogsView(p: AgentLogsViewProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <p className="max-w-2xl text-xs text-on-surface-muted">
          The Sahas Forge Agent sends a bundle of its logs and a diagnostics report daily, after an outage or a failed
          update, when asked, or by hand at the PC. Platform admins only.
        </p>
        <div className="flex flex-col gap-1 text-xs text-on-surface-muted">
          <label htmlFor="agent-logs-scope">Show the bundles of</label>
          <NativeSelect
            id="agent-logs-scope"
            className="w-44"
            value={p.scope}
            onChange={(e) => p.onScope(e.target.value === "all" ? "all" : "org")}
          >
            <option value="org">this org&apos;s PCs</option>
            <option value="all">every org&apos;s PCs</option>
          </NativeSelect>
        </div>
      </div>
      {p.state === "loading" ? (
        <p className="flex items-center gap-2 text-sm text-on-surface-muted">
          <Loader2 aria-hidden className="size-4 animate-spin" /> Loading the log bundles.
        </p>
      ) : p.state === "error" ? (
        <p className="text-sm text-danger">{p.error ?? "Couldn't load the log bundles."}</p>
      ) : p.groups.length === 0 ? (
        <p className="text-sm text-on-surface-muted">
          {p.scope === "org"
            ? "No connected PCs and no log bundles in this org yet."
            : "No agent has sent a log bundle yet."}
        </p>
      ) : (
        <>
          {p.groups.map((g) => {
            const ask = p.asks[g.agentId];
            const presence = g.agent && g.agent.status !== "revoked" ? presenceWords(g.agent) : null;
            const availability = askAvailability(g.agent, p.scope, p.agentsState);
            // Ask again once an ask is waiting or was given up on; the label names the PC after the visible words.
            const again = ask?.state === "pending" || ask?.state === "expired";
            return (
              <Card key={g.agentId} data-agent={g.agentId} className="flex flex-col gap-3 p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-on-surface">{g.name}</h3>
                    <div className="flex flex-wrap items-center gap-x-2 text-xs text-on-surface-muted">
                      <span className="font-mono">{g.identityId}</span>
                      {p.scope === "all" && <span>· plant {plantLabel(g.orgId, g.orgName)}</span>}
                      {presence && (
                        <span className="flex items-center gap-1" title={presence.title} data-presence={presence.tone}>
                          ·
                          <span aria-hidden className={cn("size-2 rounded-full", PRESENCE_DOT[presence.tone])} />
                          {presence.text}
                        </span>
                      )}
                    </div>
                  </div>
                  {availability.can ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={ask?.state === "sending"}
                      aria-label={again ? `Ask again for logs from ${g.name}` : `Ask for logs now from ${g.name}`}
                      onClick={() => p.onAsk(g)}
                    >
                      {ask?.state === "sending" ? <Loader2 aria-hidden className="animate-spin" /> : <ScrollText aria-hidden />}
                      {again ? "Ask again" : "Ask for logs now"}
                    </Button>
                  ) : (
                    <p className="text-xs text-on-surface-muted" data-ask-unavailable="">
                      {availability.reason}
                    </p>
                  )}
                </div>
                <AskLine ask={ask} onOpen={p.onOpen} />
                <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_16rem]">
                  <BundlesTable
                    bundles={g.bundles}
                    now={p.now}
                    onOpen={p.onOpen}
                    onDownload={p.onDownload}
                    downloading={p.downloading}
                  />
                  {p.history && <div>{p.history(g)}</div>}
                </div>
              </Card>
            );
          })}
          {p.truncated && (
            <p className="text-xs text-on-surface-muted">Showing the newest {AGENT_LOGS_LIMIT} bundles.</p>
          )}
        </>
      )}
    </div>
  );
}

// ── The online history ───────────────────────────────────────────────────────

export function AgentHistoryView({
  state,
  error,
  events,
}: {
  state: "loading" | "error" | "ready" | "elsewhere";
  error?: string;
  events: AgentPresenceEvent[];
}) {
  const lines = presenceEventWords(events);
  return (
    <section aria-label="Online history" className="flex flex-col gap-1.5 text-xs">
      <h4 className="font-semibold uppercase tracking-wide text-on-surface-muted">Online history</h4>
      {state === "elsewhere" ? (
        <p className="text-on-surface-muted">Shown for the org you are in: open this PC&apos;s org to see it.</p>
      ) : state === "loading" ? (
        <p className="text-on-surface-muted">Loading.</p>
      ) : state === "error" ? (
        <p className="text-danger">{error ?? "Couldn't load the online history."}</p>
      ) : lines.length === 0 ? (
        <p className="text-on-surface-muted">No changes recorded yet.</p>
      ) : (
        <ol className="flex max-h-64 flex-col gap-1 overflow-y-auto">
          {lines.map((l, i) => (
            <li key={`${l.at}-${i}`} className="flex items-start gap-1.5">
              <ToneIcon tone={l.tone} />
              <span>
                <span className="text-on-surface">{l.text}</span>
                <span className="block text-on-surface-muted">{fmtWhen(l.at)}</span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/**
 * A PC's online history, read when it is the active org's (the route is org-scoped). While the org's PCs are loading or
 * could not be read, it says so: a PC not yet among them is not in another org.
 */
export function AgentHistory({ group, agentsState }: { group: AgentLogGroup; agentsState: AgentsState }) {
  const q = useAgentPresenceQuery(group.agentId, group.agent !== undefined);
  const blocked = historyBlocked(group.agent !== undefined, agentsState);
  if (blocked) return <AgentHistoryView state={blocked.state} error={blocked.error} events={[]} />;
  return (
    <AgentHistoryView
      state={q.isError ? "error" : q.isSuccess ? "ready" : "loading"}
      error={q.isError ? logsErrorWords(q.error, "Couldn't load the online history.") : undefined}
      events={q.data?.events ?? []}
    />
  );
}

// ── A bundle's panel (pure) ──────────────────────────────────────────────────

export interface LogBundleViewProps {
  bundle: AgentLogBundle;
  plant?: string;
  state: "loading" | "error" | "ready";
  error?: string;
  diagnostics?: AgentLogDiagnostics;
  downloading: boolean;
  onDownload: () => void;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2" aria-label={title}>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-on-surface-muted">{title}</h3>
      {children}
    </section>
  );
}

export function LogBundleView(p: LogBundleViewProps) {
  const b = p.bundle;
  const report = p.state === "ready" ? p.diagnostics?.diagnostics : undefined;
  const cc = report?.connection_check;
  const verdict = report ? checkVerdict(report) : null;
  const health = report ? healthWords(report) : null;
  const outage = report ? lastOutageWords(report) : "";
  const update = report ? updateWordsOf(report) : "";
  const cert = cc?.certificate ?? cc?.problem?.certificate ?? null;
  const direct = report?.health?.direct_problem;
  return (
    <div className="flex flex-col gap-5 p-5 text-sm">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-xs">
        <dt className="text-on-surface-muted">Sent</dt>
        <dd className="text-on-surface">{fmtWhen(b.created_at)}</dd>
        <dt className="text-on-surface-muted">Why</dt>
        <dd className="text-on-surface" data-reason={b.reason}>
          {reasonSentence(b.reason, report)}
        </dd>
        {p.plant && (
          <>
            <dt className="text-on-surface-muted">Plant</dt>
            <dd className="text-on-surface">{p.plant}</dd>
          </>
        )}
        <dt className="text-on-surface-muted">Size</dt>
        <dd className="text-on-surface">{fmtSize(b.size)}</dd>
        <dt className="text-on-surface-muted">Agent</dt>
        <dd className="text-on-surface">
          {b.agent_version ? `version ${b.agent_version}` : "version not given"}
          {b.via && `, sent ${viaWords(b.via)}`}
        </dd>
      </dl>
      <div>
        <Button size="sm" variant="secondary" disabled={p.downloading} onClick={p.onDownload}>
          {p.downloading ? <Loader2 aria-hidden className="animate-spin" /> : <Download aria-hidden />}
          Download the bundle
        </Button>
      </div>

      {p.state === "loading" ? (
        <p className="flex items-center gap-2 text-on-surface-muted">
          <Loader2 aria-hidden className="size-4 animate-spin" /> Reading the report in the bundle.
        </p>
      ) : p.state === "error" || !report ? (
        <p className="text-danger">{p.error ?? "Couldn't read the report in the bundle."}</p>
      ) : (
        <>
          <Section title="Connection checks">
            {report.made_at && (
              <p className="text-xs text-on-surface-muted">
                Run when the bundle was made, {fmtWhen(report.made_at)}
                {report.agent?.pc_name ? `, on ${report.agent.pc_name}` : ""}
                {report.agent?.windows ? ` (${report.agent.windows})` : ""}
                {cc?.source_ip ? `, from ${cc.source_ip}` : ""}.
              </p>
            )}
            {verdict ? (
              <p className={cn("font-medium", verdict.ok ? "text-success" : "text-danger")} data-verdict={verdict.ok ? "ok" : "fail"}>
                {verdict.text}
              </p>
            ) : (
              <p className="text-on-surface-muted">This report has no connection check.</p>
            )}
            {cc?.checks && cc.checks.length > 0 && (
              <ul className="flex flex-col gap-1.5" aria-label="The checks">
                {cc.checks.map((c, i) => {
                  const w = checkWords(c);
                  return (
                    <li key={`${c.label}-${i}`} className="flex items-start gap-2" data-check={w.tone}>
                      <ToneIcon tone={w.tone} />
                      <span>
                        <span className="text-on-surface">{w.name}</span>
                        <span className={cn("font-medium", TONE_TEXT[w.tone])}> · {w.outcome}</span>
                        {w.detail && <span className="block text-xs text-on-surface-muted">{w.detail}</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
            {cc?.problem && (
              <div className="flex flex-col gap-1 rounded-md border border-danger/40 bg-danger/5 p-3 text-xs" data-problem={cc.problem.code}>
                <p className="text-sm font-medium text-on-surface">{problemHeadline(cc.problem, cc.certificate)}</p>
                {cc.problem.title && <p className="text-on-surface">{cc.problem.title}</p>}
                {cc.problem.advice && (
                  <p className="text-on-surface">
                    <span className="font-medium">What to ask the plant&apos;s IT:</span> {cc.problem.advice}
                  </p>
                )}
                {cc.problem.detail && (
                  <p className="break-words font-mono text-on-surface-muted">The agent saw: {cc.problem.detail}</p>
                )}
              </div>
            )}
            {cert && <p className="text-xs text-on-surface">Certificate presented: {certWords(cert)}.</p>}
            <p className="text-xs text-on-surface" data-proxy={report.proxy ? "set" : "none"}>
              {proxyWords(report)}
            </p>
            {cc?.proxy && <p className="text-xs text-on-surface-muted">{cc.proxy}</p>}
          </Section>

          <Section title="The agent then">
            {health && (
              <p className={cn("flex items-start gap-2", TONE_TEXT[health.tone])}>
                <ToneIcon tone={health.tone} />
                {health.text}
              </p>
            )}
            {direct && (
              <p className="text-xs text-on-surface">
                Its direct connection failed: {problemHeadline(direct)}. It reached Sahas Forge another way.
              </p>
            )}
            {outage && <p className="text-xs text-on-surface">Last outage: {outage}.</p>}
            {update && <p className="text-xs text-on-surface">Last update: {update}.</p>}
          </Section>

          {p.diagnostics && (
            <Section title="Files in the bundle">
              <ul className="flex flex-col gap-0.5 text-xs" aria-label="Files in the bundle">
                {p.diagnostics.files.map((f) => (
                  <li key={f.name} className="flex justify-between gap-3">
                    <span className="break-all font-mono text-on-surface">{f.name}</span>
                    <span className="whitespace-nowrap text-on-surface-muted">{fmtSize(f.size)}</span>
                  </li>
                ))}
              </ul>
              {p.diagnostics.file_count > p.diagnostics.files.length && (
                <p className="text-xs text-on-surface-muted">
                  And {p.diagnostics.file_count - p.diagnostics.files.length} more.
                </p>
              )}
              <p className="text-xs text-on-surface-muted">The logs themselves are in the download.</p>
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function LogBundleSheet({
  bundle,
  plant,
  downloading,
  onDownload,
  onClose,
}: {
  bundle: AgentLogBundle | null;
  plant?: string;
  downloading: string | null;
  onDownload: (b: AgentLogBundle) => void;
  onClose: () => void;
}) {
  const q = useAgentLogDiagnosticsQuery(bundle?.id ?? null, bundle !== null);
  if (!bundle) return null;
  return (
    <Sheet open onClose={onClose} widthClass="max-w-xl" ariaLabel={`Log bundle of ${bundle.agent_name}`}>
      <SheetHeader title="Log bundle" subtitle={`${bundle.agent_name} · ${bundle.identity_id}`} onClose={onClose} />
      <LogBundleView
        bundle={bundle}
        plant={plant}
        state={q.isError ? "error" : q.isSuccess ? "ready" : "loading"}
        error={q.isError ? logsErrorWords(q.error, "Couldn't read the report in the bundle.") : undefined}
        diagnostics={q.data}
        downloading={downloading === bundle.id}
        onDownload={() => onDownload(bundle)}
      />
    </Sheet>
  );
}

// ── Container ────────────────────────────────────────────────────────────────

function without<T>(m: Record<string, T>, key: string): Record<string, T> {
  const next = { ...m };
  delete next[key];
  return next;
}

/** The asks made in one org: they are forgotten when the org you are in changes. */
interface AsksOfOrg {
  org: string | null;
  byAgent: Record<string, Ask>;
}

/** The Logs tab, for platform admins only: nothing (and no read) for anyone else. */
export function AgentLogsPanel({ agents, agentsState }: { agents: IngestAgent[]; agentsState: AgentsState }) {
  const { isPlatformAdmin, activeOrgId } = useOrg();
  const toast = useToast();
  const [scope, setScope] = useState<LogsScope>("org");
  const [asked, setAsked] = useState<AsksOfOrg>({ org: activeOrgId, byAgent: {} });
  const [open, setOpen] = useState<AgentLogBundle | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  // The asks belong to the org they were made in: change the org you are in and they are forgotten, and with them the
  // reading of the list every 10 s they asked for. (State set while rendering, once, when the org differs: React
  // renders again at once, before anything of the stale render is shown.)
  if (asked.org !== activeOrgId) setAsked({ org: activeOrgId, byAgent: {} });
  const asks = asked.byAgent;
  // Read every 10 s while a PC asked for its logs has not sent them, for at most ASK_WAIT_MS.
  const logs = useAgentLogsQuery(scope === "org" ? activeOrgId : null, isPlatformAdmin, (items) =>
    anyWaiting(asks, items),
  );
  const request = useRequestAgentLogs();
  const items = useMemo(() => logs.data?.items ?? [], [logs.data]);

  if (!isPlatformAdmin) return null;

  /** Records a step of an ask made in `org`; dropped if the org you are in has changed since, so a late answer is not kept. */
  const put = (org: string | null, agentId: string, next: Ask | null) =>
    setAsked((s) =>
      s.org !== org ? s : { org, byAgent: next ? { ...s.byAgent, [agentId]: next } : without(s.byAgent, agentId) },
    );

  const ask = async (g: AgentLogGroup) => {
    const org = activeOrgId;
    const known = new Set(items.filter((b) => b.agent_id === g.agentId && b.reason === "request").map((b) => b.id));
    put(org, g.agentId, { state: "sending" });
    try {
      const r = await request.mutateAsync(g.agentId);
      if (r.status !== "active") {
        put(org, g.agentId, null);
        toast.push(
          "error",
          r.status === "revoked"
            ? `${g.name} is revoked: it can't send logs.`
            : `${g.name} is not set up yet: no PC can send its logs.`,
        );
        return;
      }
      const offline = g.agent?.presence?.state === "offline";
      put(org, g.agentId, { state: "pending", at: Date.now(), offline, known });
    } catch (err) {
      put(org, g.agentId, null);
      toast.push("error", logsErrorWords(err, `Couldn't ask ${g.name} for its logs.`));
    }
  };

  const download = async (b: AgentLogBundle) => {
    setDownloading(b.id);
    try {
      await downloadAgentLog(b);
    } catch (err) {
      toast.push("error", logsErrorWords(err, "Couldn't download the bundle."));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <>
      <AgentLogsView
        scope={scope}
        onScope={setScope}
        state={logs.isError ? "error" : logs.isSuccess ? "ready" : "loading"}
        error={logs.isError ? logsErrorWords(logs.error, "Couldn't load the log bundles.") : undefined}
        groups={logGroups(scope, agents, items, activeOrgId)}
        truncated={items.length >= AGENT_LOGS_LIMIT}
        now={logs.dataUpdatedAt}
        agentsState={agentsState}
        asks={askStates(asks, items)}
        onAsk={(g) => void ask(g)}
        onOpen={setOpen}
        onDownload={(b) => void download(b)}
        downloading={downloading}
        history={(g) => <AgentHistory group={g} agentsState={agentsState} />}
      />
      <LogBundleSheet
        bundle={open}
        plant={open && scope === "all" ? plantLabel(open.org_id, open.org_name) : undefined}
        downloading={downloading}
        onDownload={(b) => void download(b)}
        onClose={() => setOpen(null)}
      />
    </>
  );
}
